import { useIntl } from '@/intl'
import { DepartureAirportFilterSummary } from '@/scopes/search/components/DepartureAirportFilterComponent'
import { DepartureDateFilterSummary } from '@/scopes/search/components/DepartureDateFilter/DepartureDateFilterSummary'
import { DurationFilterSummary } from '@/scopes/search/components/DurationFilterComponent'
import { ParticipantsFilterSummary } from '@/scopes/search/components/ParticipantsFilterComponent'
import { MainLabel, QuickSearchItem, SelectionTextWrapper } from '@/scopes/search/components/QuickSearchBar/Item'
import { hasActiveItemSelector, useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { useResults } from '@/scopes/search/search/domain/hooks'
import {
  DepartureAirportQuickSearchItemFilter,
} from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/DepartureAiportFilter'
import {
  DepartureDateQuickSearchItemFilter,
} from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/DepartureDateFilter'
import {
  DurationQuickSearchItemFilter,
} from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/DurationFilter'
import {
  ParticipantsQuickSearchItemFilter,
} from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/ParticipantsFilter'
import { mqMin, radius, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, focusOutline, Icon, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { PropsWithChildren, useRef } from 'react'
import { useIntersection } from 'react-use'
import { css, styled } from 'styled-components'

export const QuickSearchBarHeight = '80px'

const InputItems = styled.div``

const QuickSearchDrawer = styled.div<{ $hasActiveItem?: boolean, $isDisabled?: boolean, $isSticky?: boolean }>`
  @media ${mqMin('primitives_breakpoint_l_min')} {
    max-width: 960px;
    box-shadow: ${tok('primitives_box_shadows_level_2')};
    background-color: ${tok('semantics_views_background_color')};
    border-radius: ${radius('primitives_corner_radius_full')}; //To change to the semantic token
    padding: ${space('primitives_space_8')};
    margin-right: auto;
    margin-left: auto;
    height: ${QuickSearchBarHeight};
    z-index: 1;
  }

  ${({ $isDisabled }) =>
    $isDisabled
    && css`
      pointer-events: none;
      opacity: ${tok('primitives_opacity_disabled')}%;
    `}

  ${InputItems} {
    display: flex;
    flex-direction: column;

    @media ${mqMin('primitives_breakpoint_l_min')} {
      flex-direction: row;
      height: 100%;

      ${QuickSearchItem} {
        &:first-of-type {
          //using space instead of full radius due to incompatibility with the designs and the implementation (rest of the borders get cut off)
          border-radius: ${space('primitives_space_32')} ${radius('primitives_corner_radius_s')}
            ${radius('primitives_corner_radius_s')} ${space('primitives_space_32')};
        }

        &:last-of-type {
          //using space instead of full radius due to incompatibility with the designs and the implementation (rest of the borders get cut off)
          border-radius: ${radius('primitives_corner_radius_s')} ${space('primitives_space_32')}
            ${space('primitives_space_32')} ${radius('primitives_corner_radius_s')};
        }
      }
    }
  }
`

const DrawerDesktop = styled(QuickSearchDrawer)`
  display: none;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    display: inherit;

    ${({ $isSticky }) => $isSticky && isStickyMod()}
  }
`

const Wrapper = styled.div`
  padding-bottom: ${space('primitives_space_48')};
  width: 100%;
  justify-content: center;
  height: ${QuickSearchBarHeight};
  align-items: center;
`

function isStickyMod() {
  return css`
    left: 0;
    position: fixed;
    top: 0;
    width: 100vw;
    max-width: inherit;
    border-radius: inherit;

    ${InputItems} {
      justify-content: center;

      ${QuickSearchItem},
      ${QuickSearchItem}:first-of-type,
      ${QuickSearchItem}:last-of-type {
        border-radius: ${radius('primitives_corner_radius_s')};
      }
    }
  `
}

const Sentinel = styled.div`
  background: transparent;
  height: 1px;
  margin-bottom: -1px;
  width: 100%;
`

const ActionWrapper = styled.div`
  position: relative;
`

const ActionFade = styled.div`
  content: '';
  position: absolute;
  top: -${space('primitives_space_12')};
  left: -${space('primitives_space_24')};
  width: 100%;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 40%);
  height: calc(100% + ${space('primitives_space_24')});
`

const Action = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${space('primitives_space_44')};
  min-width: ${space('primitives_space_44')};
  background-color: ${tok('semantics_buttons_secondary_background_color')};
  border-radius: ${tokPx('semantics_buttons_m_corner_radius')};
  z-index: 1;
  position: relative;
`

const BarTrigger = styled.button<{ $isSticky?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: calc(100% - 2 * ${space('primitives_space_16')});
  box-shadow: ${tok('primitives_box_shadows_level_2')};
  background-color: ${tok('semantics_views_background_color')};
  border-radius: ${radius('primitives_corner_radius_full')}; //To change to the semantic token
  padding: ${space('primitives_space_12')} ${space('primitives_space_24')};
  margin-right: auto;
  margin-left: auto;
  height: ${QuickSearchBarHeight};
  cursor: pointer;
  gap: ${space('primitives_space_8')};

  ${({ $isSticky }) => $isSticky && isStickyMod()}

  @media ${mqMin('primitives_breakpoint_m_min')} {
    width: calc(100% - 2 * ${space('primitives_space_48')});

    ${({ $isSticky }) => $isSticky && isStickyMod()}
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    display: none;
  }

  &:hover {
    ${Action} {
      background-color: ${tok('semantics_buttons_secondary_hover_background_color')};
    }
  }

  ${focusOutline()}
`

const Box = styled.div`
  text-align: left;
  overflow: hidden;
`

const IconItems = styled.div`
  display: flex;
  gap: ${space('primitives_space_6')};

  ${MainLabel} {
    display: none;
  }

  ${QuickSearchItem} {
    padding: inherit;
    min-width: inherit;

    ${SelectionTextWrapper} {
      gap: ${space('primitives_space_2')};
      align-items: center;
    }

    & > ${Icon} {
      display: none;
    }

    &:before {
      display: none;
    }
  }
`

export function QuickSearchBar({ disabled, className }: PropsWithChildren<{ disabled?: boolean, className?: string }>) {
  const intl = useIntl()
  const hasActiveItem = useQuickSearchStore(hasActiveItemSelector)
  const { totalResults } = useResults()

  const sentinelRef = useRef<HTMLDivElement>(null)
  const observer = useIntersection(sentinelRef, {
    root: null, // Defaults to the browser viewport
    rootMargin: '0px',
    threshold: 1,
  })

  return (
    <Wrapper className={className}>
      <Sentinel ref={sentinelRef} />
      <DrawerDesktop
        $hasActiveItem={hasActiveItem}
        $isDisabled={disabled}
        $isSticky={observer ? !observer.isIntersecting : false}
      >
        <InputItems>
          <DepartureDateQuickSearchItemFilter id="departure-date-filter" />
          <DurationQuickSearchItemFilter id="travel-duration-filter" />
          <ParticipantsQuickSearchItemFilter id="participants-filter" />
          <DepartureAirportQuickSearchItemFilter id="departure-airport-duration-filter" />
        </InputItems>
      </DrawerDesktop>
      <Sheet.Root>
        <Sheet.Trigger>
          <BarTrigger $isSticky={observer ? !observer.isIntersecting : false}>
            <Box>
              <Typography $variant="body-m-bold-tight">{intl.formatMessage({ id: 'quicksearch_title' })}</Typography>
              <IconItems>
                <DepartureDateFilterSummary />
                <DurationFilterSummary />
                <ParticipantsFilterSummary />
                <DepartureAirportFilterSummary />
              </IconItems>
            </Box>
            <ActionWrapper>
              <ActionFade />
              <Action>
                <Icon glyph="Pencil" />
              </Action>
            </ActionWrapper>
          </BarTrigger>
        </Sheet.Trigger>
        <Sheet.Body side="right" hasPadding>
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'quicksearch_sheet_header' })}
          />
          <Sheet.Content>
            <QuickSearchDrawer>
              <InputItems>
                <DepartureDateQuickSearchItemFilter id="mobile-departure-date-filter" asSheet />
                <DurationQuickSearchItemFilter id="mobile-travel-duration-filter" asSheet />
                <ParticipantsQuickSearchItemFilter id="mobile-participants-filter" asSheet />
                <DepartureAirportQuickSearchItemFilter id="mobile-departure-duration-filter" asSheet />
              </InputItems>
            </QuickSearchDrawer>
          </Sheet.Content>
          <Sheet.Footer isFixed>
            <Sheet.Close>
              <Button $type="primary" $fullWidth>
                {intl.formatMessage({ id: 'quicksearch_results' }, { nrOfResults: totalResults })}
              </Button>
            </Sheet.Close>
          </Sheet.Footer>
        </Sheet.Body>
      </Sheet.Root>
    </Wrapper>
  )
}
export { PlaceholderText } from '@/scopes/search/components/QuickSearchBar/Item'
export { SelectionTextWrapper } from '@/scopes/search/components/QuickSearchBar/Item'
export { QuickSearchItem } from '@/scopes/search/components/QuickSearchBar/Item'
